"use client";

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { Category, Service } from "@/types";
import { cn } from "@/lib/utils";

const pillClassCard =
	"inline-flex max-w-[11rem] shrink-0 snap-start items-center truncate rounded-full bg-secondary/5 px-2 py-1 text-[9px] font-normal leading-tight text-secondary";

const pillClassCompactCard =
	"inline-flex max-w-[8.5rem] shrink-0 snap-start items-center truncate rounded-full bg-secondary/5 px-1.5 py-0.5 text-[8px] font-normal leading-tight text-secondary";

const pillClassServicePage =
	"inline-flex max-w-[14rem] shrink-0 snap-start items-center truncate rounded-full bg-secondary/5 px-2.5 py-1.5 text-xs font-normal leading-tight text-secondary";

function itemsFromSubcategories(subs: Service["subcategoryIds"]): { id: string; title: string }[] {
	if (!Array.isArray(subs)) return [];
	const out: { id: string; title: string }[] = [];
	for (const c of subs) {
		if (!c || typeof c !== "object") continue;
		const cat = c as Category;
		const title = typeof cat.title === "string" ? cat.title.trim() : "";
		if (title.length === 0) continue;
		const id = cat._id != null && String(cat._id).length > 0 ? String(cat._id) : title;
		out.push({ id, title });
	}
	return out;
}

type Props = {
	service: Service;
	/** Extra classes on the outer wrapper (e.g. negative margin for padding alignment). */
	className?: string;
	/** Trailing scroll-fade gradient (defaults to card surface; use page background on service detail). */
	fadeEdgeClassName?: string;
	/** `servicePage` = larger pills + “Designed for:” label (service detail only). */
	variant?: "card" | "compactCard" | "servicePage";
};

/**
 * Single-line subcategory pills; horizontal scroll when needed with a trailing fade
 * until the user scrolls to the end.
 */
const DEFAULT_FADE_EDGE =
	"from-card via-card/85 to-transparent dark:from-card dark:via-card/85";

export function ServiceCardSubcategoryPills({
	service,
	className,
	fadeEdgeClassName,
	variant = "card",
}: Props) {
	const items = useMemo(() => itemsFromSubcategories(service.subcategoryIds), [service.subcategoryIds]);
	const scrollRef = useRef<HTMLDivElement>(null);
	const [fadeRight, setFadeRight] = useState(false);
	const isServicePage = variant === "servicePage";
	const isCompactCard = variant === "compactCard";
	const rowHeight = isServicePage ? "h-8" : isCompactCard ? "h-5" : "h-7";
	const pillClass = isServicePage
		? pillClassServicePage
		: isCompactCard
			? pillClassCompactCard
			: pillClassCard;

	const syncFade = useCallback(() => {
		const el = scrollRef.current;
		if (!el) {
			setFadeRight(false);
			return;
		}
		const canScroll = el.scrollWidth > el.clientWidth + 1;
		const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
		setFadeRight(canScroll && !atEnd);
	}, []);

	useLayoutEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		syncFade();
		const ro = new ResizeObserver(() => {
			requestAnimationFrame(syncFade);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, [items, syncFade]);

	if (items.length === 0) {
		return <div className={cn(rowHeight, "min-w-0 shrink-0", className)} aria-hidden />;
	}

	const pillsTrack = (
		<div className={cn("relative min-h-0 min-w-0 flex-1", rowHeight)}>
			<div
				ref={scrollRef}
				onScroll={syncFade}
				className={cn(
					"flex h-full min-h-0 min-w-0 flex-nowrap items-center overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
					isCompactCard ? "gap-1" : "gap-1.5",
				)}
				aria-label="Subcategories"
			>
				{items.map((item) => (
					<span key={item.id} className={pillClass} title={item.title}>
						{item.title}
					</span>
				))}
			</div>
			<div
				aria-hidden
				className={cn(
					"pointer-events-none absolute inset-y-0 right-0 z-[1] w-9 bg-gradient-to-l transition-opacity duration-200",
					fadeEdgeClassName ?? DEFAULT_FADE_EDGE,
					fadeRight ? "opacity-100" : "opacity-0",
				)}
			/>
		</div>
	);

	if (!isServicePage) {
		return (
			<div className={cn("relative min-w-0 shrink-0", rowHeight, className)}>{pillsTrack}</div>
		);
	}

	return (
		<div className={cn("flex min-w-0 items-center gap-2", rowHeight, className)}>
			<span className="shrink-0 text-xs font-bold leading-tight text-grey-foreground">
				Designed for:
			</span>
			{pillsTrack}
		</div>
	);
}
